import { withNamespaces } from 'react-i18next';
import { Table } from 'reactstrap';

import { getCountryFlag } from '../../constants';
import { EMPTY_LIST } from 'src/common/constants';

const COUNTRY_NAMES = {
    AT: 'Austria',
    CH: 'Switzerland',
    DE: 'Germany',
};

const emptyValue = (value) => (value === null || value === undefined || value === '' ? '—' : value);

const countryLabel = (country) => COUNTRY_NAMES[country] || country;

const SectionCard = ({
    children,
    title,
    t,
}) => (
    <div className="company-report-card">
        {title && <h6 className="company-report-card-title">{t(title)}</h6>}
        {children}
    </div>
);

const formatRegister = (register) => {
    const parts = [register.register, register.number].filter((part) => part && part !== '—');

    if (!parts.length) return '—';

    return register.court && register.court !== '—'
        ? `${parts.join(' ')} (${register.court})`
        : parts.join(' ');
};

const InfoTable = ({
    rows,
    t,
}) => (
    <Table borderless className="mb-0 company-details-table" size="sm">
        <tbody>
            {rows.map((row) => (
                <tr key={row.label}>
                    <th>{t(row.label)}</th>
                    <td>
                        {row.link && row.value && row.value !== '—' ? (
                            <a href={row.link} rel="noreferrer" target="_blank">
                                {row.value}
                            </a>
                        ) : emptyValue(row.value)}
                    </td>
                </tr>
            ))}
        </tbody>
    </Table>
);

const PeopleTable = ({
    columns,
    emptyLabel,
    people,
    t,
}) => {
    if (!people.length) {
        return <p className="text-muted mb-0">{t(emptyLabel || EMPTY_LIST)}</p>;
    }

    return (
        <Table className="mb-0" size="sm" striped>
            <thead>
                <tr>
                    {columns.map((column) => (
                        <th key={column.key}>{t(column.label)}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {people.map((person, index) => (
                    <tr key={`${person.name}-${index}`}>
                        {columns.map((column) => (
                            <td key={column.key}>{emptyValue(person[column.key])}</td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </Table>
    );
};

export const CompanyInformation = withNamespaces()(({
    identity,
    t,
}) => (
    <div>
        <h5 className="mb-1">{t('Company Information')}</h5>
        <p className="text-muted mb-4">
            {t('Basic information and legal details about this company.')}
        </p>

        <SectionCard t={t} title="Identity & Legal Information">
            <InfoTable
                rows={[
                    { label: 'Legal Name', value: identity.legalName },
                    { label: 'Legal Form', value: identity.legalForm },
                    { label: 'NACE Code', value: identity.nace },
                    { label: 'Primary industry', value: identity.industry },
                    {
                        label: 'Commercial Register',
                        value: formatRegister(identity.register),
                    },
                    { label: 'VAT Number', value: identity.vatNumber },
                    { label: 'Registration Date', value: identity.established },
                    { label: 'Employees', value: identity.employees ? identity.employees.toLocaleString('de-DE') : '—' },
                    { label: 'Signatory rules', value: identity.signatoryRules },
                ]}
                t={t}
            />
        </SectionCard>

        <SectionCard t={t} title="Contact Information">
            <InfoTable
                rows={[
                    { label: 'Address', value: identity.address },
                    { label: 'Country', value: identity.countryName },
                    { label: 'Website', value: identity.website, link: identity.websiteUrl },
                    { label: 'Email', value: identity.email },
                    { label: 'Phone', value: identity.phone },
                ]}
                t={t}
            />
        </SectionCard>
    </div>
));

export const FinancialSummary = withNamespaces()(({
    identity,
    t,
}) => (
    <div>
        <h5 className="mb-1">{t('Financial Summary')}</h5>
        <p className="text-muted mb-4">
            {t('Available financial indicators for this company.')}
        </p>

        <SectionCard t={t}>
            <Table className="mb-0" size="sm" striped>
                <thead>
                    <tr>
                        <th>{t('Indicator')}</th>
                        <th>{t('Value')}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{t('Estimated revenue')}</td>
                        <td>{identity.revenue}</td>
                    </tr>
                    <tr>
                        <td>{t('Employees')}</td>
                        <td>{identity.employees ? identity.employees.toLocaleString('de-DE') : '—'}</td>
                    </tr>
                    <tr>
                        <td>{t('Currency')}</td>
                        <td>EUR</td>
                    </tr>
                </tbody>
            </Table>
        </SectionCard>
    </div>
));

export const ManagementTeam = withNamespaces()(({
    identity,
    t,
}) => (
    <div>
        <h5 className="mb-1">{t('Management Team')}</h5>
        <p className="text-muted mb-4">
            {t('Directors and listed contacts associated with this company.')}
        </p>
        <SectionCard t={t}>
            <PeopleTable
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'role', label: 'Role' },
                    { key: 'country', label: 'Country' },
                ]}
                people={identity.management.map((person) => ({
                    ...person,
                    country: countryLabel(person.country),
                }))}
                t={t}
            />
        </SectionCard>
    </div>
));

export const Shareholders = withNamespaces()(({
    identity,
    t,
}) => (
    <div>
        <h5 className="mb-1">{t('Subcontractors')}</h5>
        <p className="text-muted mb-4">
            {t('Known subcontractors of this company.')}
        </p>
        <SectionCard t={t}>
            <PeopleTable
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'type', label: 'Type' },
                    { key: 'share', label: 'Share' },
                ]}
                people={identity.shareholders}
                t={t}
            />
        </SectionCard>
    </div>
));

export const UltimateShareholders = withNamespaces()(({
    identity,
    t,
}) => (
    <div>
        <h5 className="mb-1">{t('Ultimate Shareholders')}</h5>
        <p className="text-muted mb-4">
            {t('Ultimate beneficial owners or controlling shareholders.')}
        </p>
        <SectionCard t={t}>
            <PeopleTable
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'type', label: 'Type' },
                    { key: 'share', label: 'Share' },
                ]}
                people={identity.ultimateShareholders}
                t={t}
            />
        </SectionCard>
    </div>
));

export const Subsidiaries = withNamespaces()(({
    identity,
    t,
}) => (
    <div>
        <h5 className="mb-1">{t('Subsidiaries')}</h5>
        <p className="text-muted mb-4">
            {t('Related operating companies and group entities.')}
        </p>
        <SectionCard t={t}>
            <PeopleTable
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'country', label: 'Country' },
                    { key: 'relation', label: 'Relation' },
                ]}
                people={identity.subsidiaries.map((company) => ({
                    ...company,
                    country: countryLabel(company.country),
                }))}
                t={t}
            />
        </SectionCard>
    </div>
));

export const sectionComponents = {
    'company-information': CompanyInformation,
    'financial-summary': FinancialSummary,
    'management-team': ManagementTeam,
    shareholders: Shareholders,
    'ultimate-shareholders': UltimateShareholders,
    subsidiaries: Subsidiaries,
};

export const CountryFlag = ({
    country,
    countryName,
}) => (
    <span className="d-flex align-items-center gap-2">
        <img
            alt={countryName}
            className="country-flag"
            src={getCountryFlag(country)}
        />
        {countryName}
    </span>
);
